import { ContactAttributeFormatTypes } from '@app/redux/contactAttributes/types';
import { NICHE_MAJOR_UUIDS, NICHE_MAJOR_UUIDS_TO_LABELS } from './nicheMajors';
import { Major } from '@app/redux/workspace/types';
import {
	getCompletedMilestoneLabel,
	getCompletedMilestoneOptions,
} from './modules/milestones';
import {
	US_STATE_ABBREVIATIONS,
	US_STATE_ABBREVIATIONS_TO_NAMES,
} from './usStates';

const completedMilestoneOptions = getCompletedMilestoneOptions();

export type NicheAttribute = {
	format: ContactAttributeFormatTypes;
	path: string;
	pathNoDot: string;
	pathSplit: string[];
	valueToAttributeLabel?: (value: any) => string;
	label: string;
	choices: string[];
	choiceLabels?: {
		[value: string]: string;
	};
};

export const nicheAttributes: NicheAttribute[] = [
	{
		label: 'Niche contact gender',
		format: ContactAttributeFormatTypes.TEXT,
		path: 'customAttributes.nicheAttributes.gender',
		pathNoDot: 'customAttributes-nicheAttributes-gender',
		pathSplit: ['customAttributes', 'nicheAttributes', 'gender'],
		choices: ['female', 'male', 'other', 'i-prefer-not-to-answer'],
		choiceLabels: {
			female: 'Female',
			male: 'Male',
			other: 'Other',
			'i-prefer-not-to-answer': 'I prefer not to answer',
		},
	},
	{
		label: 'Niche contact race',
		format: ContactAttributeFormatTypes.TEXT,
		path: 'customAttributes.nicheAttributes.race',
		pathNoDot: 'customAttributes-nicheAttributes-race',
		pathSplit: ['customAttributes', 'nicheAttributes', 'race'],
		choices: [
			'african-american',
			'asian',
			'hispanic',
			'pacific-islander',
			'multiracial',
			'native-american',
			'white',
			'no-answer',
		],
		choiceLabels: {
			'african-american': 'Black / African American',
			asian: 'Asian',
			hispanic: 'Hispanic',
			'pacific-islander': 'Pacific Islander',
			multiracial: 'Multiracial',
			'native-american': 'Native American',
			white: 'White',
			'no-answer': 'I prefer not to answer',
		},
	},
	{
		label: 'Niche contact religion',
		format: ContactAttributeFormatTypes.TEXT,
		path: 'customAttributes.nicheAttributes.religion',
		pathNoDot: 'customAttributes-nicheAttributes-religion',
		pathSplit: ['customAttributes', 'nicheAttributes', 'religion'],
		choices: [
			'adventist',
			'african-methodist',
			'agnostic',
			'anglican',
			'assembly-of-god',
			'atheist',
			'bahai',
			'baptist',
			'buddhist',
			'catholic',
			'christian-and-missionary-alliance',
			'christian-science',
			'churches-of-christ',
			'church-of-brethren',
			'church-of-christ',
			'church-of-god',
			'church-of-scientology',
			'church-of-the-nazarene',
			'confucian',
			'congregational',
			'disciples-of-christ',
			'episcopal',
			'evangelical-covenant-church',
			'evangelical-free-church',
			'greek-orthodox',
			'hindu',
			'i-prefer-not-to-answer',
			'islam',
			'jehovahs-witness',
			'jewish',
			'lds',
			'lutheran',
			'mennonite',
			'methodist',
			'missionary-church',
			'moravian',
			'nondenominational-christian',
			'none',
			'other-not-listed',
			'pagan-wiccan',
			'pentecostal',
			'presbyterian',
			'protestant',
			'quaker',
			'reformed',
			'russian-orthodox',
			'sikh',
			'taoist',
			'unitarian-universalist',
			'wesleyan',
		],
		choiceLabels: {
			adventist: 'Adventist',
			'african-methodist': 'African Methodist',
			agnostic: 'Agnostic',
			anglican: 'Anglican',
			'assembly-of-god': 'Assembly of God',
			atheist: 'Atheist',
			bahai: "Baha'i",
			baptist: 'Baptist',
			buddhist: 'Buddhist',
			catholic: 'Catholic',
			'christian-and-missionary-alliance': 'Christian and Missionary Alliance',
			'christian-science': 'Christian Science',
			'church-of-brethren': 'Church of Brethren',
			'church-of-christ': 'Church of Christ',
			'church-of-god': 'Church of God',
			'church-of-scientology': 'Church of Scientology',
			'church-of-the-nazarene': 'Church of the Nazarene',
			'churches-of-christ': 'Churches of Christ',
			confucian: 'Confucian',
			congregational: 'Congregational',
			'disciples-of-christ': 'Disciples of Christ',
			episcopal: 'Episcopal',
			'evangelical-covenant-church': 'Evangelical Covenant Church',
			'evangelical-free-church': 'Evangelical Free Church',
			'greek-orthodox': 'Greek Orthodox',
			hindu: 'Hindu',
			'i-prefer-not-to-answer': 'I prefer not to answer',
			islam: 'Islam',
			'jehovahs-witness': "Jehovah's Witness",
			jewish: 'Jewish',
			lds: 'LDS',
			lutheran: 'Lutheran',
			mennonite: 'Mennonite',
			methodist: 'Methodist',
			'missionary-church': 'Missionary Church',
			moravian: 'Moravian',
			'nondenominational-christian': 'Nondenominational Christian',
			none: 'None',
			'other-not-listed': 'Other/Not Listed',
			'pagan-wiccan': 'Pagan/Wiccan',
			pentecostal: 'Pentecostal',
			presbyterian: 'Presbyterian',
			protestant: 'Protestant',
			quaker: 'Quaker',
			reformed: 'Reformed',
			'russian-orthodox': 'Russian Orthodox',
			sikh: 'Sikh',
			taoist: 'Taoist',
			'unitarian-universalist': 'Unitarian Universalist',
			wesleyan: 'Wesleyan',
		},
	},
	{
		label: 'Niche contact home state',
		format: ContactAttributeFormatTypes.TEXT,
		path: 'customAttributes.nicheAttributes.homeStateAbbr',
		pathNoDot: 'customAttributes-nicheAttributes-homeStateAbbr',
		pathSplit: ['customAttributes', 'nicheAttributes', 'homeStateAbbr'],
		choices: US_STATE_ABBREVIATIONS,
		choiceLabels: US_STATE_ABBREVIATIONS_TO_NAMES,
	},
	{
		label: 'Niche contact high school GPA',
		format: ContactAttributeFormatTypes.NUMBER,
		path: 'customAttributes.nicheAttributes.highSchoolGPA',
		pathNoDot: 'customAttributes-nicheAttributes-highSchoolGPA',
		pathSplit: ['customAttributes', 'nicheAttributes', 'highSchoolGPA'],
		choices: [],
	},
	{
		label: 'Niche contact high school grad year',
		format: ContactAttributeFormatTypes.NUMBER,
		path: 'customAttributes.nicheAttributes.highSchoolGradYear',
		pathNoDot: 'customAttributes-nicheAttributes-highSchoolGradYear',
		pathSplit: ['customAttributes', 'nicheAttributes', 'highSchoolGradYear'],
		choices: [],
	},
	{
		label: 'Niche contact SAT composite',
		format: ContactAttributeFormatTypes.NUMBER,
		path: 'customAttributes.nicheAttributes.satComposite',
		pathNoDot: 'customAttributes-nicheAttributes-satComposite',
		pathSplit: ['customAttributes', 'nicheAttributes', 'satComposite'],
		choices: [],
	},

	{
		label: 'Niche contact ACT composite',
		format: ContactAttributeFormatTypes.NUMBER,
		path: 'customAttributes.nicheAttributes.actComposite',
		pathNoDot: 'customAttributes-nicheAttributes-actComposite',
		pathSplit: ['customAttributes', 'nicheAttributes', 'actComposite'],
		choices: [],
	},
	{
		label: 'Niche contact birthdate',
		format: ContactAttributeFormatTypes.DATE,
		path: 'customAttributes.nicheAttributes.birthdate',
		pathNoDot: 'customAttributes-nicheAttributes-birthdate',
		pathSplit: ['customAttributes', 'nicheAttributes', 'birthdate'],
		choices: [],
	},
	{
		label: 'Niche contact core lead status',
		format: ContactAttributeFormatTypes.TEXT,
		path: 'customAttributes.nicheAttributes.coreStatus',
		pathNoDot: 'customAttributes-nicheAttributes-coreStatus',
		pathSplit: ['customAttributes', 'nicheAttributes', 'coreStatus'],
		choices: ['Prospect', 'QualifiedInquiry'],
		choiceLabels: {
			Prospect: 'Prospect',
			QualifiedInquiry: 'Qualified Inquiry',
		},
	},
	{
		label: 'Niche contact direct admissions status',
		format: ContactAttributeFormatTypes.TEXT,
		path: 'customAttributes.nicheAttributes.daStatus',
		pathNoDot: 'customAttributes-nicheAttributes-daStatus',
		pathSplit: ['customAttributes', 'nicheAttributes', 'daStatus'],
		choices: ['Prospect', 'Admitted', 'Onboarded'],
		choiceLabels: {
			Prospect: 'Prospect',
			Admitted: 'Admitted',
			Onboarded: 'Onboarded',
		},
	},
	{
		label: 'Niche contact fit score',
		format: ContactAttributeFormatTypes.NUMBER,
		path: 'customAttributes.nicheAttributes.fitScore',
		pathNoDot: 'customAttributes-nicheAttributes-fitScore',
		pathSplit: ['customAttributes', 'nicheAttributes', 'fitScore'],
		choices: [],
	},
	{
		label: 'Niche contact interested majors',
		format: ContactAttributeFormatTypes.ARRAY,
		// path must use the uuid field specifically as the path to filter
		// interestedMajors by.
		path: 'customAttributes.nicheAttributes.interestedMajors.uuid',
		// pathNoDot and pathSplit can use the parent `interestedMajors` field
		// as their path since these paths are used only on already fetched
		// contacts.
		pathNoDot: 'customAttributes-nicheAttributes-interestedMajors',
		pathSplit: ['customAttributes', 'nicheAttributes', 'interestedMajors'],
		valueToAttributeLabel: (value: Major[]) => {
			if (!Array.isArray(value) || !value.length) {
				return '-';
			}

			return value
				.map(
					(item) => item.name || NICHE_MAJOR_UUIDS_TO_LABELS[item.uuid] || '-',
				)
				.join(', ');
		},
		choices: NICHE_MAJOR_UUIDS, // See if this can be fetched from entity instead of hard coding.
		choiceLabels: NICHE_MAJOR_UUIDS_TO_LABELS,
	},
	{
		label: 'Niche contact completed milestones',
		format: ContactAttributeFormatTypes.ARRAY,
		path: 'completedMilestones',
		pathNoDot: 'completedMilestones',
		pathSplit: ['completedMilestones'],
		valueToAttributeLabel: (value: string[]) => {
			if (!Array.isArray(value) || !value.length) {
				return '-';
			}

			return value.map((item) => getCompletedMilestoneLabel(item)).join(', ');
		},
		choices: completedMilestoneOptions.map((option) => option.value),
		choiceLabels: Object.fromEntries(
			completedMilestoneOptions.map((option) => [option.value, option.label]),
		),
	},
];

/*
  Rendered separately from `nicheAttributes` because its visibility is gated
  by the `advance-competitive-standing-messaging` entitlement, independent of
  the `areNicheAttributesDisplayable` flag that gates every other attribute
  above.
 */
export const competitiveStandingAttribute: NicheAttribute = {
	label: 'Niche contact competitive standing',
	format: ContactAttributeFormatTypes.TEXT,
	path: 'customAttributes.nicheAttributes.competitiveStanding',
	pathNoDot: 'customAttributes-nicheAttributes-competitiveStanding',
	pathSplit: ['customAttributes', 'nicheAttributes', 'competitiveStanding'],
	choices: ['Not enough data', 'Trailing', 'Competing', 'Leading'],
};

/*
  Rendered separately from `nicheAttributes` because they are only needed where
  audience rules are read and built — keeping them out of
  that list avoids adding rows to the contact details slide and merge tags for
  values that only make sense as segment filters.
 */

export const nicheStageDateAttributes: NicheAttribute[] = [
	{
		label: 'Niche contact core prospect date',
		format: ContactAttributeFormatTypes.DATE,
		path: 'customAttributes.nicheAttributes.stageTimestamps.core.prospect',
		pathNoDot: 'customAttributes-nicheAttributes-stageTimestamps-core-prospect',
		pathSplit: [
			'customAttributes',
			'nicheAttributes',
			'stageTimestamps',
			'core',
			'prospect',
		],
		choices: [],
	},
	{
		label: 'Niche contact core qualified inquiry date',
		format: ContactAttributeFormatTypes.DATE,
		path: 'customAttributes.nicheAttributes.stageTimestamps.core.qualifiedInquiry',
		pathNoDot:
			'customAttributes-nicheAttributes-stageTimestamps-core-qualifiedInquiry',
		pathSplit: [
			'customAttributes',
			'nicheAttributes',
			'stageTimestamps',
			'core',
			'qualifiedInquiry',
		],
		choices: [],
	},
	{
		label: 'Niche contact direct admissions prospect date',
		format: ContactAttributeFormatTypes.DATE,
		path: 'customAttributes.nicheAttributes.stageTimestamps.da.prospect',
		pathNoDot: 'customAttributes-nicheAttributes-stageTimestamps-da-prospect',
		pathSplit: [
			'customAttributes',
			'nicheAttributes',
			'stageTimestamps',
			'da',
			'prospect',
		],
		choices: [],
	},
	{
		label: 'Niche contact direct admissions qualified inquiry date',
		format: ContactAttributeFormatTypes.DATE,
		path: 'customAttributes.nicheAttributes.stageTimestamps.da.qualifiedInquiry',
		pathNoDot:
			'customAttributes-nicheAttributes-stageTimestamps-da-qualifiedInquiry',
		pathSplit: [
			'customAttributes',
			'nicheAttributes',
			'stageTimestamps',
			'da',
			'qualifiedInquiry',
		],
		choices: [],
	},
	{
		label: 'Niche contact application start date',
		format: ContactAttributeFormatTypes.DATE,
		path: 'customAttributes.nicheAttributes.crm_applicationStartDate',
		pathNoDot: 'customAttributes-nicheAttributes-crm_applicationStartDate',
		pathSplit: [
			'customAttributes',
			'nicheAttributes',
			'crm_applicationStartDate',
		],
		choices: [],
	},
	{
		label: 'Niche contact application completed date',
		format: ContactAttributeFormatTypes.DATE,
		path: 'customAttributes.nicheAttributes.crm_applicationCompletedDate',
		pathNoDot: 'customAttributes-nicheAttributes-crm_applicationCompletedDate',
		pathSplit: [
			'customAttributes',
			'nicheAttributes',
			'crm_applicationCompletedDate',
		],
		choices: [],
	},
	{
		label: 'Niche contact application submitted date',
		format: ContactAttributeFormatTypes.DATE,
		path: 'customAttributes.nicheAttributes.crm_applicationSubmittedDate',
		pathNoDot: 'customAttributes-nicheAttributes-crm_applicationSubmittedDate',
		pathSplit: [
			'customAttributes',
			'nicheAttributes',
			'crm_applicationSubmittedDate',
		],
		choices: [],
	},
];

/*
  A flat value -> label lookup across every niche attribute's choiceLabels.
  Used by the filter display helpers to label stored values without depending
  on the in-memory selection state, which is empty when a saved segment is
  reopened.
 */
export const completedMilestonesAttribute = nicheAttributes.find(
	(attribute) => attribute.path === 'completedMilestones',
);

export const nicheAttributeChoiceLabels: Record<string, string> =
	nicheAttributes.reduce(
		(labels, attr) => ({ ...labels, ...attr.choiceLabels }),
		{},
	);
